// The personalized write-up on a scoring v4 results page, spread across the page's sections
// instead of sitting in one block: each part goes to the section it is about, next to the
// scored content that shows at once. While it is written, each place shows a shimmering
// skeleton and one bar under the hero (in the four domain colours) says how it is going.
//
// Every word the model returns is shown somewhere. A part with a heading this page doesn't
// place (a newer prompt, "What others see" before v4 has raters) and anything written before
// the first heading go to the "More written for you" block at the end of the last section.

import type { ReactNode } from "react";
import { domainColor } from "../lib/domainColors";
import { Diamond } from "./Diamond";
import type { WriteupView } from "../lib/types";
import { FOUR_COLOURS, Inline, Paragraph, WritingProgress } from "./PersonalWriteup";

/** Above each written part: it says a model wrote it (audit K8). */
export const WRITTEN_BY_AI = "Written by AI for you";

/** The parts this page places, by the heading writeup-prompt v2 gives them. */
export const WRITEUP_PLACES = {
  built: "How you're built",
  gifts: "How your gifts are showing up right now",
  connect: "How you connect with others",
  way: "What's in the way",
  /** Only with raters (the v3.1 test, two or more finished). */
  others: "What others see",
  next: "Your next step",
} as const;
export type WriteupPlace = keyof typeof WRITEUP_PLACES;

const PLACED = new Set<string>(Object.values(WRITEUP_PLACES));

type Part = { heading: string; paragraphs: string[] };

/** What the page shows of the write-up: written (the parts), being written, or neither. */
export type WriteupState =
  | { kind: "ready"; part: (place: WriteupPlace) => Part | null; leftovers: { preamble: string[]; parts: Part[] } }
  | { kind: "pending" }
  | { kind: "none" };

/** Sorts the write-up into places. Read-only copies never show "pending" (they don't ask again). */
export function writeupState(view: WriteupView | null | undefined, readOnly: boolean): WriteupState {
  if (!view) return { kind: "none" };
  if (view.status === "pending") return readOnly ? { kind: "none" } : { kind: "pending" };
  const preamble = view.preamble ?? [];
  if (view.status !== "ready" || (view.sections.length === 0 && preamble.length === 0)) return { kind: "none" };
  return {
    kind: "ready",
    part: (place) => view.sections.find((s) => s.heading === WRITEUP_PLACES[place]) ?? null,
    leftovers: { preamble, parts: view.sections.filter((s) => !PLACED.has(s.heading)) },
  };
}

/** Under the hero: the bar while it is written, or the one line when it couldn't be. */
export function WriteupStatus({ view, gaveUp, readOnly }: { view: WriteupView | null | undefined; gaveUp: boolean; readOnly: boolean }) {
  if (readOnly || !view) return null;
  if (view.status === "pending") {
    return (
      <section className="mt-8 rounded-[16px] border border-line bg-panel px-4 pb-4 pt-1" aria-label="Your personalized results" data-testid="writeup-status">
        <WritingProgress gaveUp={gaveUp} readyNote="Your scores below are ready now." />
      </section>
    );
  }
  if (view.status === "fallback") {
    return (
      <p className="mt-8 rounded-[16px] border border-line bg-soft p-4 text-[15px] leading-snug text-muted" data-testid="writeup-status">
        We couldn't write your personalized parts this time. Everything else on this page is your full result.
      </p>
    );
  }
  return null;
}

/** A shimmering stand-in for a part that is being written. */
function Skeleton({ lines = [100, 94, 97, 68] }: { lines?: number[] }) {
  return (
    <div className="mt-3 flex flex-col gap-2.5" aria-hidden="true">
      {lines.map((w, i) => (
        <div key={i} className="skeleton h-3 rounded-full" style={{ width: `${w}%` }} />
      ))}
    </div>
  );
}

/** The thin bar running along the top of a part that is being written. */
function Runner() {
  return (
    <div className="runner relative h-[3px] overflow-hidden rounded-full bg-line" aria-hidden="true">
      <span style={{ backgroundImage: FOUR_COLOURS }} />
    </div>
  );
}

/**
 * One part of the write-up inside a section, in a soft panel labelled "Written by AI for you": the
 * paragraphs once written, a skeleton while it is, nothing otherwise.
 */
export function WriteupSlot({ state, place, showHeading = true }: { state: WriteupState; place: WriteupPlace; showHeading?: boolean }) {
  const heading = WRITEUP_PLACES[place];
  if (state.kind === "pending") {
    return (
      <div className="mt-6 rounded-[16px] bg-soft p-4" aria-busy="true" data-testid={`writeup-${place}`}>
        <Runner />
        <p className="eyebrow mt-3 text-accent-text">{WRITTEN_BY_AI}{showHeading ? ` · ${heading}` : ""}</p>
        <p className="mt-1 text-[14px] text-muted">Writing this part…</p>
        <Skeleton />
      </div>
    );
  }
  if (state.kind !== "ready") return null;
  const part = state.part(place);
  if (!part || part.paragraphs.length === 0) return null;
  return (
    <div className="writeup-in mt-6 rounded-[16px] bg-soft p-4 print:break-inside-avoid" data-testid={`writeup-${place}`}>
      <p className="eyebrow text-accent-text">{WRITTEN_BY_AI}{showHeading ? ` · ${heading}` : ""}</p>
      {part.paragraphs.map((paragraph, i) => (
        <Paragraph key={i} text={paragraph} />
      ))}
    </div>
  );
}

/** "**With Relators:** …": the domain a "How you connect with others" paragraph is about, if any. */
const WITH_DOMAIN = /^\*\*With (Strategists|Relators|Executors|Influencers)\b([^*]*?):?\*\*:?\s*([\s\S]*)$/;
/** Where each "**With …" piece starts: the model sometimes puts all of them in one paragraph. */
const WITH_START = /(?=\*\*With (?:Strategists|Relators|Executors|Influencers)\b)/;
const DOMAIN_OF: Record<string, string> = { Strategists: "STR", Relators: "REL", Executors: "EXE", Influencers: "INF" };

/** A paragraph cut into its pieces: plain text, and each "**With …:**" piece on its own. */
export const connectPieces = (paragraph: string): string[] =>
  paragraph
    .split(WITH_START)
    .map((piece) => piece.trim())
    .filter(Boolean);

/**
 * "How you connect with others", a section of its own: the advice for each domain in its own
 * card, in that domain's colour; every other paragraph as written.
 */
export function ConnectPart({ state }: { state: WriteupState }) {
  if (state.kind === "pending") {
    return (
      <div className="rounded-[16px] bg-soft p-4" aria-busy="true" data-testid="writeup-connect">
        <Runner />
        <p className="mt-3 text-[14px] text-muted">Writing this part…</p>
        <Skeleton lines={[100, 92, 74]} />
        <div className="mt-4 flex flex-col gap-3" aria-hidden="true">
          {(["REL", "EXE", "INF"] as const).map((code) => (
            <div key={code} className="flex items-center gap-2.5">
              <Diamond color={domainColor(code).fill} />
              <div className="skeleton h-3 flex-1 rounded-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (state.kind !== "ready") return null;
  const part = state.part("connect");
  if (!part) return null;
  return (
    <div className="writeup-in" data-testid="writeup-connect">
      {part.paragraphs.flatMap((paragraph, i) =>
        connectPieces(paragraph).map((piece, j) => {
          const key = `${i}.${j}`;
          const m = WITH_DOMAIN.exec(piece);
          if (!m) return <Paragraph key={key} text={piece} />;
          const color = domainColor(DOMAIN_OF[m[1]!]!);
          return (
            <div key={key} className="mt-3 rounded-[16px] border border-line bg-panel p-4 print:break-inside-avoid">
              <p className="flex items-center gap-2 text-[15px] font-bold" style={{ color: color.text }}>
                <Diamond color={color.fill} />
                With {m[1]}
                {m[2]}:
              </p>
              <p className="mt-1.5 text-[16px] leading-snug">
                <Inline text={m[3]!} />
              </p>
            </div>
          );
        }),
      )}
    </div>
  );
}

/** Whatever the page doesn't place, so nothing the model wrote goes unseen. */
export function WriteupLeftovers({ state }: { state: WriteupState }) {
  if (state.kind !== "ready") return null;
  const { preamble, parts } = state.leftovers;
  if (preamble.length === 0 && parts.length === 0) return null;
  return (
    <div className="writeup-in mt-6 rounded-[16px] bg-soft p-4 print:break-inside-avoid" data-testid="writeup-more">
      <p className="eyebrow text-accent-text">More written for you</p>
      {preamble.map((paragraph, i) => (
        <Paragraph key={`p${i}`} text={paragraph} />
      ))}
      {parts.map((part) => (
        <div key={part.heading} className="mt-4">
          <h3 className="heading text-[20px] leading-tight">{part.heading}</h3>
          {part.paragraphs.map((paragraph, i) => (
            <Paragraph key={i} text={paragraph} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** The profile text in the ink card, inside "What this means and how to use it". */
export function MeaningPanel({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-[16px] bg-card p-6 text-card-text sm:p-8 print:break-inside-avoid">
      <p className="eyebrow text-card-accent">What this means</p>
      <p className="mt-3 text-[19px] leading-snug">{children}</p>
    </div>
  );
}
